import { NoodleQuestionDto } from '@/app/common/general-types/noodle.dto';

/** Where a creator has got to against the brief. */
export type ScreeningVerdict = 'passed' | 'flagged' | 'failed' | 'progress' | 'queued';

/**
 * How serious a breached rule is. Derived from the rule's `questionSubType`, not supplied by
 * Noodle — so when the rule can't be resolved it is `unrated` rather than a guessed middle value.
 */
export type FlagSeverity = 'high' | 'medium' | 'low' | 'unrated';

/**
 * Whether the payload told us *which* rule each issue relates to, or only how many there were.
 *
 * This is the difference between "rule #3 was breached" and "something went wrong somewhere", and
 * it decides what the rule-by-rule scorecard is allowed to claim.
 */
export type RuleAttribution = 'per-rule' | 'counts-only';

/**
 * A rule's outcome for one creator.
 *
 * `unattributed` exists so we never say "Met" about a rule when we know issues were found but not
 * where — it renders as "Not assessed" with a note explaining why.
 */
export type RuleOutcome = 'met' | 'breached' | 'notAssessed' | 'unattributed';

/** A breached restriction vs. a required rule that was never met. */
export type FlagKind = 'breach' | 'missing';

/**
 * One rule that a single piece of content failed.
 *
 * Every optional field is one the Noodle payload may simply not carry. Absent means "render
 * nothing here" — never zero, never a placeholder. The adapter is the only thing that fills these.
 */
export interface ScreeningFlag {
  /** Stable key for tracking; `${contentId}::${ruleId}`. */
  id: string;
  contentId: string;
  /** Noodle's creator id, when the payload attributes the content to a creator. */
  creatorId?: string;
  /** Resolved from the brief's rule list by id. */
  rule?: NoodleQuestionDto;
  /** 1-based position in the rule list, for the "#03" display. */
  ruleIndex?: number;
  /** Falls back to the rule's statement when the payload repeats it. */
  statement?: string;
  kind: FlagKind;
  severity: FlagSeverity;
  /** The AI's plain-English explanation. */
  finding?: string;
  /** Evidence lifted from the content. */
  quote?: string;
  /** Where in the content the breach occurs. */
  at?: string;
  /** Only set when the payload carries a link — gates the Watch action. */
  contentUrl?: string;
}

/** A creator's progress through the brief, derived from the per-creator results. */
export interface ScreeningCreatorProgress {
  evaluated: number;
  total: number;
  /** Pieces of content with at least one issue. */
  flaggedContent: number;
  /** Total issues across all of this creator's content. */
  issues: number;
  /** 0-100. */
  completion: number;
  verdict: ScreeningVerdict;
}

/** How a content item should be drawn from its `contentUrls`. */
export type ScreeningMediaKind = 'video' | 'image' | 'carousel' | 'none';

/** Content-tab filter. `issues` hides posts that passed every rule. */
export type ContentView = 'all' | 'issues';

/** One evaluated piece of content, as much as the payload lets us describe it. */
export interface ScreeningContentItem {
  id: string;
  rulesTotal: number;
  rulesPassed: number;
  issues: number;
  /** Rules Noodle marked approved. Distinct from `rulesTotal - issues`. */
  approvedCount: number;
  /** Direct media files for this item, in slide order. */
  contentUrls: readonly string[];
  mediaKind: ScreeningMediaKind;
  /** 'pending' until it has been evaluated at all. */
  verdict: 'pass' | 'flag' | 'fail' | 'pending';
  flags: ScreeningFlag[];
  contentUrl?: string;
}

/** A post that raised a red flag, joined to the creator who posted it. */
export interface FlaggedCohortContent {
  item: ScreeningContentItem;
  creatorName: string;
  initials: string;
  /** Instagram or TikTok handle, including the @. */
  handle?: string;
  noodleCreatorId?: string;
  /** 1-based position in this creator's content list. */
  contentIndex: number;
  contentTotal: number;
}

/** The hub's four KPI cells. */
export interface ScreeningKpis {
  creators: number;
  evaluated: number;
  totalContent: number;
  flags: number;
  /** Flags still awaiting a decision. Equal to `flags` until decisions exist. */
  openFlags: number;
  /** 0-100 across the whole cohort. */
  cohortCompletion: number;
}

/** Empty progress for a creator Noodle has never heard of. */
export const EMPTY_PROGRESS: ScreeningCreatorProgress = {
  evaluated: 0,
  total: 0,
  flaggedContent: 0,
  issues: 0,
  completion: 0,
  verdict: 'queued',
};
